import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Paper, TextField, IconButton, Tooltip, Typography, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useFirebase } from '../../context/FirebaseContext';
import { getAdminErrorsV2, getAdminErrorByIdV2, deleteAdminErrorV2 } from '../../api/admin';
import logger from '../../utils/logger';
import './AdminView.css';

const ErrorsTab = ({ initialUserId = '' }) => {
    const { auth } = useFirebase();
    const [errors, setErrors] = useState([]);
    const [selectedError, setSelectedError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorFilter, setErrorFilter] = useState({ userId: initialUserId, limit: 100 });

    const loadErrors = useCallback(async () => {
        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                const userId = errorFilter.userId.trim();
                if (userId) {
                    getAdminErrorsV2(auth, resolve, userId, errorFilter.limit);
                } else {
                    getAdminErrorsV2(auth, resolve, null, errorFilter.limit);
                }
            });

            let errorData = Array.isArray(response) ? response : response.data;

            if (errorData) {
                const sortedErrors = errorData.sort((a, b) =>
                    new Date(b.createdAt * 1000) - new Date(a.createdAt * 1000)
                );
                setErrors(sortedErrors);
            } else {
                setErrors([]);
            }
        } catch (error) {
            logger.error('Error loading errors:', error);
            setErrors([]);
        } finally {
            setLoading(false);
        }
    }, [auth, errorFilter]);

    useEffect(() => {
        loadErrors();
    }, [loadErrors]);

    useEffect(() => {
        if (initialUserId) {
            setErrorFilter(prev => ({ ...prev, userId: initialUserId }));
        }
    }, [initialUserId]);

    const handleViewError = async (errorId) => {
        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                getAdminErrorByIdV2(auth, resolve, errorId);
            });
            if (response && !response.error) {
                setSelectedError(response);
            } else {
                logger.error('Error loading error details:', response.error);
            }
        } catch (error) {
            logger.error('Error loading error details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteError = async (errorId) => {
        try {
            setLoading(true);
            await new Promise((resolve) => {
                deleteAdminErrorV2(auth, resolve, errorId);
            });
            loadErrors();
            if (selectedError?.errorId === errorId) {
                setSelectedError(null);
            }
        } catch (error) {
            logger.error('Error deleting error log:', error);
        } finally {
            setLoading(false);
        }
    };

    const truncateMessage = (msg, max = 80) => {
        if (!msg) return 'No message';
        return msg.length > max ? msg.substring(0, max) + '...' : msg;
    };

    if (loading && errors.length === 0) {
        return (
            <Box className="admin-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="admin-tab-content">
            {/* Filters */}
            <Box className="admin-filters">
                <TextField
                    label="Filter by User ID"
                    value={errorFilter.userId}
                    onChange={(e) => setErrorFilter(prev => ({ ...prev, userId: e.target.value }))}
                    placeholder="Leave empty for all"
                    size="small"
                    className="admin-filter-input"
                />
                <TextField
                    label="Limit"
                    type="number"
                    value={errorFilter.limit}
                    onChange={(e) => setErrorFilter(prev => ({
                        ...prev,
                        limit: Math.max(1, parseInt(e.target.value) || 100)
                    }))}
                    size="small"
                    inputProps={{ min: 1 }}
                    className="admin-filter-limit"
                />
                <Button variant="outlined" onClick={loadErrors} className="admin-filter-btn">
                    Apply
                </Button>
            </Box>

            {/* Desktop Table View */}
            <Box className="admin-table-desktop">
                <TableContainer component={Paper} className="admin-table-container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Error ID</TableCell>
                                <TableCell className="admin-hide-mobile">User ID</TableCell>
                                <TableCell>Model</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell className="admin-hide-mobile">Created</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {errors.map((error) => (
                                <TableRow key={error.errorId} className="admin-table-row">
                                    <TableCell className="admin-id-cell">{error.errorId}</TableCell>
                                    <TableCell className="admin-hide-mobile">{error.userId}</TableCell>
                                    <TableCell>{error.modelId}</TableCell>
                                    <TableCell className="admin-message-cell">
                                        {truncateMessage(error.errorMessage)}
                                    </TableCell>
                                    <TableCell className="admin-hide-mobile">
                                        {new Date(error.createdAt * 1000).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        <Box className="admin-actions">
                                            <Tooltip title="View Details">
                                                <IconButton onClick={() => handleViewError(error.errorId)} size="small">
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton onClick={() => handleDeleteError(error.errorId)} size="small" color="error">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {errors.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No errors found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Mobile Card View */}
            <Box className="admin-cards-mobile">
                {errors.length === 0 && (
                    <Paper className="admin-empty-state">
                        <Typography>No errors found</Typography>
                    </Paper>
                )}
                {errors.map((error) => (
                    <Paper key={error.errorId} className="admin-error-card">
                        <Box className="admin-error-card-header">
                            <span className="admin-error-id">#{error.errorId}</span>
                            <span className="admin-error-model">{error.modelId}</span>
                        </Box>
                        <Box className="admin-error-card-body">
                            <Typography className="admin-error-message">
                                {truncateMessage(error.errorMessage, 120)}
                            </Typography>
                            <span className="admin-error-date">
                                {new Date(error.createdAt * 1000).toLocaleString()}
                            </span>
                        </Box>
                        <Box className="admin-error-card-actions">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewError(error.errorId)}
                            >
                                View
                            </Button>
                            <Button
                                variant="text"
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteError(error.errorId)}
                            >
                                Delete
                            </Button>
                        </Box>
                    </Paper>
                ))}
            </Box>

            {/* Error Details Dialog */}
            <Dialog
                open={!!selectedError}
                onClose={() => setSelectedError(null)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { maxHeight: '90vh' } }}
                className="admin-dialog"
            >
                <DialogTitle>
                    <Box className="admin-dialog-header">
                        <Typography variant="h6">Error Details</Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            {selectedError && new Date(selectedError.createdAt * 1000).toLocaleString()}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedError && (
                        <Box className="admin-error-details">
                            <Paper className="admin-detail-section">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography><strong>Error ID:</strong> {selectedError.errorId}</Typography>
                                        <Typography><strong>User ID:</strong> {selectedError.userId}</Typography>
                                        <Typography><strong>Chat ID:</strong> {selectedError.chatId}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography><strong>Model:</strong> {selectedError.modelId}</Typography>
                                        <Typography><strong>Category:</strong> {selectedError.metadata?.category || 'N/A'}</Typography>
                                        <Typography><strong>Symbol:</strong> {selectedError.metadata?.symbol || 'N/A'}</Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Typography variant="subtitle1" className="admin-detail-label">Message Content</Typography>
                            <Paper className="admin-detail-code">
                                <pre>{selectedError.messageContent}</pre>
                            </Paper>

                            <Typography variant="subtitle1" className="admin-detail-label">Error Message</Typography>
                            <Paper className="admin-detail-code admin-detail-error">
                                <pre>{selectedError.errorMessage}</pre>
                            </Paper>

                            <Typography variant="subtitle1" className="admin-detail-label">Stack Trace</Typography>
                            <Paper className="admin-detail-code admin-detail-stack">
                                <pre>{selectedError.stackTrace}</pre>
                            </Paper>

                            {selectedError.metadata && (
                                <>
                                    <Typography variant="subtitle1" className="admin-detail-label">Metadata</Typography>
                                    <Paper className="admin-detail-code">
                                        <pre>{JSON.stringify(selectedError.metadata, null, 2)}</pre>
                                    </Paper>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => handleDeleteError(selectedError?.errorId)}
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Delete Error
                    </Button>
                    <Button onClick={() => setSelectedError(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ErrorsTab;
